import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/* ============================================================
   CUSTOM VALIDATION for Watch Tag Custom Text
   ============================================================ */

function validateCustomTextInForm(form) {
  if (!form) return true;

  const wrap = form.querySelector('#wt-custom-text');
  const input = form.querySelector('#wtc-input');
  const counter = form.querySelector('#wtc-counter');
  const errorEl = form.querySelector('#wtc-error');

  if (!wrap || !input) return true; // no custom block present

  const ERR_EMPTY = wrap.dataset.errEmpty || "Please enter text.";
  const ERR_LONG  = wrap.dataset.errLong  || "Maximum 30 characters.";

  const val = (input.value || "").trim();

  function showError(msg) {
    if (errorEl) errorEl.textContent = msg;
    if (counter) counter.classList.add("wtc-counter--bad");
    input.classList.add("wtc-input--error");
    input.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  function clearError() {
    if (errorEl) errorEl.textContent = "";
    if (counter) counter.classList.remove("wtc-counter--bad");
    input.classList.remove("wtc-input--error");
  }

  clearError();

  if (!val) {
    showError(ERR_EMPTY);
    return false;
  }

  if (val.length > 30) {
    showError(ERR_LONG);
    return false;
  }

  return true;
}

function initCustomTextCounter(form) {
  const input = form?.querySelector('#wtc-input');
  const counter = form?.querySelector('#wtc-counter');
  if (!input || !counter) return;

  function update() {
    counter.textContent = input.value.length + "/30";
  }

  input.addEventListener("input", update);
  update();
}

/* ============================================================
   ADD TO CART BUTTON
   ============================================================ */

export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  #animationTimeout;
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  handleClick(event) {
    const form = this.closest('form');

    // ***** CUSTOM VALIDATION *****
    if (!validateCustomTextInForm(form)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return false;
    }
    // *****************************

    if (!form?.checkValidity()) return;

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';

    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;
    if (!image) return;
    preloadImage(image);
  };

  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = document.createElement('fly-to-cart');
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/* ============================================================
   PRODUCT FORM
   ============================================================ */

class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // INIT CUSTOM TEXT COUNTER
    initCustomTextCounter(this.querySelector('form'));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  handleSubmit(event) {
    const form = this.querySelector('form');
    if (!form) return;

    // ***** CUSTOM VALIDATION *****
    if (!validateCustomTextInForm(form)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return false;
    }
    // *****************************

    event.preventDefault();
    if (this.#timeout) clearTimeout(this.#timeout);

    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          const error = this.refs.addToCartTextError;
          if (!error) return;

          error.classList.remove('hidden');
          const txt = error.childNodes[2];
          if (txt) txt.textContent = response.message;
          else error.appendChild(document.createTextNode(response.message));

          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            error.classList.add('hidden');
            this.#clearLiveRegionText();
          }, 10000);

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        }

        const id = formData.get('id');
        if (!id) throw new Error('Form ID is required');

        const btn = this.refs.addToCartButtonContainer?.refs.addToCartButton;
        if (btn) {
          const addedText = btn.querySelector('.add-to-cart-text--added')?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), 5000);
        }

        this.dispatchEvent(
          new CartAddEvent({}, id.toString(), {
            source: 'product-form-component',
            itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
            productId: this.dataset.productId,
            sections: response.sections,
          })
        );
      })
      .catch((error) => console.error(error))
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  #setLiveRegionText(text) {
    this.refs.liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    this.refs.liveRegion.textContent = '';
  }

  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentBtn = addToCartButtonContainer?.refs.addToCartButton;
    const newBtn = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentBtn) return;

    if (event.detail.resource == null || event.detail.resource.available == false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    if (newBtn) morph(currentBtn, newBtn);

    variantId.value = event.detail.resource.id ?? '';

    const productVariantMedia = event.detail.resource?.featured_media?.preview_image?.src;
    if (productVariantMedia) {
      addToCartButtonContainer?.setAttribute(
        'data-product-variant-media',
        productVariantMedia + '&width=100'
      );
    }
  };

  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

/* ============================================================
   FLY TO CART ANIMATION
   ============================================================ */

class FlyToCart extends HTMLElement {
  source;
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destRect = this.destination.getBoundingClientRect();

    const offset = { x: rect.width / 2, y: rect.height / 2 };

    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destRect.left + destRect.width / 2 - offset.x,
      y: destRect.top + destRect.height / 2 - offset.y,
    };

    const cp1 = { x: startPoint.x,     y: startPoint.y - 200 };
    const cp2 = { x: endPoint.x - 300, y: endPoint.y - 100 };

    let startTime = null;
    const duration = 600;

    this.style.opacity = '1';

    const animate = (t) => {
      if (!startTime) startTime = t;
      const progress = Math.min((t - startTime) / duration, 1);

      const pos = bezierPoint(progress, startPoint, cp1, cp2, endPoint);

      this.style.setProperty('--x', `${pos.x}px`);
      this.style.setProperty('--y', `${pos.y}px`);

      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    requestAnimationFrame(animate);
  }
}

function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  return {
    x: aX * t ** 3 + bX * t ** 2 + cX * t + p0.x,
    y: aY * t ** 3 + bY * t ** 2 + cY * t + p0.y,
  };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}

